import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  useCreateDiseaseArea,
  useDeleteDiseaseArea,
  useDiseaseArea,
  useUpdateDiseaseArea,
} from "@/api/hooks/diseaseAreas";

export default function DiseaseAreaForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const { data: existing, isLoading } = useDiseaseArea(id);
  const create = useCreateDiseaseArea();
  const update = useUpdateDiseaseArea(id ?? "");
  const remove = useDeleteDiseaseArea();

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [masterProtocolNumber, setMasterProtocolNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existing) {
      setCode(existing.code);
      setName(existing.name);
      setMasterProtocolNumber(existing.master_protocol_number ?? "");
      setNotes(existing.notes ?? "");
    }
  }, [existing]);

  if (isEdit && isLoading) return <CircularProgress />;

  const submit = async () => {
    setError(null);
    const payload = {
      code: code.trim(),
      name: name.trim(),
      master_protocol_number: masterProtocolNumber.trim() || null,
      notes: notes.trim() || null,
    };
    try {
      if (isEdit) await update.mutateAsync(payload);
      else await create.mutateAsync(payload);
      navigate("/clinops/sites/disease-areas");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    }
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        {isEdit ? "Edit disease area" : "New disease area"}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Stack spacing={2}>
        <TextField
          label="Code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
          slotProps={{ htmlInput: { maxLength: 32 } }}
          helperText="Short code used in display IDs (e.g., DERM, GASTRO, LD)."
        />
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <TextField
          label="Master protocol number"
          value={masterProtocolNumber}
          onChange={(e) => setMasterProtocolNumber(e.target.value)}
        />
        <TextField
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          rows={3}
        />
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
          <Box>
            {isEdit && (
              <Button
                color="error"
                onClick={async () => {
                  if (!id) return;
                  if (!confirm("Delete this disease area?")) return;
                  try {
                    await remove.mutateAsync(id);
                    navigate("/clinops/sites/disease-areas");
                  } catch (e) {
                    setError(e instanceof Error ? e.message : "Failed to delete");
                  }
                }}
              >
                Delete
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={() => navigate("/clinops/sites/disease-areas")}>Cancel</Button>
            <Button
              variant="contained"
              onClick={submit}
              disabled={!code.trim() || !name.trim() || create.isPending || update.isPending}
            >
              {isEdit ? "Save" : "Create"}
            </Button>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}
